/**
 * Counting the games and site pages, with no question asked (Tom, 2026-10-15: no "remember this device"
 * banner; the How Many Words dashboard still needs to tell one player from another).
 *
 * THE BASIS: PECR Schedule A1 para 5, the statistical purposes exception the Data (Use and Access) Act 2025
 * added (ICO, "Guidance on the use of storage and access technologies", What are the exceptions?). It holds
 * only while all of these hold, so each is a rule of this file:
 *   - SOLE PURPOSE: the visitor id is used only to count how the games and pages are used, in aggregate
 *     (players, new players, hours, by game and language), to improve them. First party: our own endpoints.
 *   - NOT SHARED, NOT LINKED: it is never handed to anyone else and never joined to an account, a sign-up or
 *     a payment. The ICO names "linking visitor IDs to purchases" as outside the exception, so the sign-in
 *     link of job #603 (/api/web/link) is not made.
 *   - INFORMATION: the privacy notice (public/privacy/index.html, "Counting game plays") says what is kept
 *     and why.
 *   - A SIMPLE, FREE WAY TO OBJECT: the button on that page (public/privacy/counting.js) sets COUNTING_OFF_KEY;
 *     from then on the id is deleted and no counting event or play ping leaves this device. Remembering the
 *     objection is strictly necessary storage.
 *
 * Events also carry this page load's visit id, held in memory only. No imports, so the games' recording
 * (useWordGame.ts, guess/gameRecording.ts) and the web activity record (useWebActivity.ts) read one rule
 * without importing each other.
 */
export const VISITOR_KEY = 'ssi-visitor-id'
/** '1' = this device objected to being counted. public/privacy/counting.js writes the same key. */
export const COUNTING_OFF_KEY = 'ssi-counting-off'
/** The consent era's answer (job #603): a 'no' is carried over as an objection, then the key goes. */
const OLD_CONSENT_KEY = 'ssi-counting-consent'
/** Keys nothing uses any more: the sign-in link mark, and a sharer token once kept after a Yes. */
const RETIRED_KEYS = ['ssi-visitor-linked', 'ssi-go-token']

export function store(): Storage | null {
  try {
    return localStorage
  } catch {
    return null
  }
}

export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** This page load's id: memory only, gone when the page closes. */
let visit: string | null = null
export function visitId(): string {
  return (visit ??= uuid())
}
/** Test seam. */
export function resetVisit(): void {
  visit = null
}

/** Has this device objected to being counted? */
export function countingOff(): boolean {
  try {
    return store()?.getItem(COUNTING_OFF_KEY) === '1'
  } catch {
    return false
  }
}

/** Object (true) or stop objecting (false). Objecting deletes the visitor id at once. */
export function setCountingOff(off: boolean): void {
  const s = store()
  if (!s) return
  try {
    if (off) {
      s.setItem(COUNTING_OFF_KEY, '1')
      s.removeItem(VISITOR_KEY)
    } else s.removeItem(COUNTING_OFF_KEY)
  } catch {
    /* nothing to keep it in */
  }
}

/**
 * The counting id: created on first use, kept in localStorage (not a cookie, so it travels only with our
 * counting events). null once the device has objected, or where storage is blocked.
 */
export function visitorId(): string | null {
  if (countingOff()) return null
  const s = store()
  if (!s) return null
  try {
    let id = s.getItem(VISITOR_KEY)
    if (!id || !/^[A-Za-z0-9_-]{16,64}$/.test(id)) {
      id = uuid().replace(/-/g, '')
      s.setItem(VISITOR_KEY, id)
    }
    return id
  } catch {
    return null
  }
}

/** On load (App.vue): an old No becomes an objection; keys nothing reads are deleted. */
export function tidyCountingKeys(s: Storage | null = store()): void {
  if (!s) return
  try {
    if (s.getItem(OLD_CONSENT_KEY) === 'no') setCountingOff(true)
    s.removeItem(OLD_CONSENT_KEY)
    for (const k of RETIRED_KEYS) s.removeItem(k)
    if (s.getItem(COUNTING_OFF_KEY) === '1') s.removeItem(VISITOR_KEY)
  } catch {
    /* nothing kept */
  }
}
